/* 
  File: src/pages/stb/StbEditStellenanzeige.js
  Zweck: Erfassungsmaske für ausführliche Stellenanzeigen.
         Basisdaten (aus stb_suchanzeigen) sowie sprachspezifische Daten (aus stb_suchanzeige_lang)
         werden erfasst. Zusätzlich können Berufsbezeichnung und Arbeitsform ausgewählt werden.
         Für die ausklappbaren Fullscreen-Editoren wird ReactQuill verwendet.
*/

import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../../config/config";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

// Beispiel-Komponenten für moderne Input-Felder
const Input = ({ label, ...props }) => (
  <div className="mb-4">
    <label className="block text-white mb-1">{label}</label>
    <input className="w-full p-2 border rounded bg-gray-800 text-white" {...props} />
  </div>
);

const Select = ({ label, options, ...props }) => (
  <div className="mb-4">
    <label className="block text-white mb-1">{label}</label>
    <select className="w-full p-2 border rounded bg-gray-800 text-white" {...props}>
      <option value="">Bitte wählen</option>
      {options.map(opt => (
        <option key={opt.id} value={opt.id}>{opt.name}</option>
      ))}
    </select>
  </div>
);

// Standardtoolbar für ReactQuill
const quillModules = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["clean"]
  ]
};

const StbEditStellenanzeige = () => {
  const { id } = useParams(); // "new" oder existierende Anzeige-ID
  const navigate = useNavigate();

  // Basisdaten (stb_suchanzeigen)
  const [baseData, setBaseData] = useState({
    employerId: "",
    title: "",
    description: "",
    skills: "",
    region: "",
    specialStatus: 0,
    status: "inactive",
  });

  // Sprachspezifische Daten (stb_suchanzeige_lang)
  const [langData, setLangData] = useState({
    job_description: "",
    qualifications: "",
    additional_info: "",
    beruf_id: "",
    arbeitsform_id: "",
    language: "de",
  });

  // Dropdown-Daten für Berufsbezeichnungen und Arbeitsformen
  const [berufOptions, setBerufOptions] = useState([]);
  const [arbeitsformOptions, setArbeitsformOptions] = useState([]);

  useEffect(() => {
    axios.get(`${config.API_BASE_URL}/api/berufsbezeichnung`)
      .then(response => {
        const data = Array.isArray(response.data)
          ? response.data.map(b => ({ id: b.id, name: b.beruf }))
          : [];
        setBerufOptions(data);
      })
      .catch(error => console.error("Fehler beim Laden der Berufsbezeichnungen:", error));

    axios.get(`${config.API_BASE_URL}/api/arbeitsform`)
      .then(response => {
        const data = Array.isArray(response.data)
          ? response.data.map(a => ({ id: a.id, name: a.arbeitsform }))
          : [];
        setArbeitsformOptions(data);
      })
      .catch(error => console.error("Fehler beim Laden der Arbeitsformen:", error));
  }, []);

  useEffect(() => {
    if (id && id !== "new") {
      // Lade Basisdaten aus stb_suchanzeigen
      axios.get(`${config.API_BASE_URL}/api/stb/suchanzeigen/${id}`)
        .then(response => {
          setBaseData(response.data);
        })
        .catch(error => console.error("Fehler beim Laden der Basisdaten:", error));
      
      // Lade sprachspezifische Daten aus stb_suchanzeige_lang
      axios.get(`${config.API_BASE_URL}/api/stb/suchanzeige_lang/${id}?lang=de`)
        .then(response => {
          setLangData(response.data);
        })
        .catch(error => console.error("Fehler beim Laden der sprachspezifischen Daten:", error));
    }
  }, [id]);

  const handleBaseChange = (e) => {
    const { name, value, type, checked } = e.target;
    setBaseData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? (checked ? 1 : 0) : value,
    }));
  };

  const handleLangChange = (e) => {
    const { name, value } = e.target;
    setLangData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleQuillChange = (name, value) => {
    setLangData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    try {
      // Stelle sicher, dass die PUT-Requests mit dem richtigen Content-Type gesendet werden
      const axiosConfig = { headers: { "Content-Type": "application/json" } };

      if (id && id !== "new") {
        // Aktualisiere Basisdaten in stb_suchanzeigen
        await axios.put(`${config.API_BASE_URL}/api/stb/suchanzeigen/${id}`, baseData, axiosConfig);
        // Aktualisiere sprachspezifische Daten in stb_suchanzeige_lang
        await axios.put(`${config.API_BASE_URL}/api/stb/suchanzeige_lang/${id}`, langData, axiosConfig);
      } else {
        // Neue Anzeige anlegen
        const resBase = await axios.post(`${config.API_BASE_URL}/api/stb/suchanzeigen`, baseData, axiosConfig);
        const newId = resBase.data.id;
        await axios.post(`${config.API_BASE_URL}/api/stb/suchanzeige_lang`, { ...langData, suchanzeige_id: newId }, axiosConfig);
      }
      navigate("/dashboard/employer");
    } catch (error) {
      console.error("Fehler beim Speichern der Anzeige:", error);
      alert("Fehler beim Speichern der Anzeige.");
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center p-5 bg-gray-900">
      <div className="bg-black bg-opacity-50 p-8 rounded-lg shadow-lg w-full max-w-4xl text-white">
        <h2 className="text-3xl font-bold mb-6 text-center">Stellenanzeige erfassen</h2>
        {/* Basisdaten */}
        <form className="space-y-4">
          <Input 
            label="Jobtitel" 
            name="title" 
            value={baseData.title} 
            onChange={handleBaseChange} 
            required 
          />
          <Input 
            label="Kurzbeschreibung" 
            name="description" 
            value={baseData.description} 
            onChange={handleBaseChange} 
            required 
          />
          <Input 
            label="Skills" 
            name="skills" 
            value={baseData.skills} 
            onChange={handleBaseChange} 
          />
          <Input 
            label="Region" 
            name="region" 
            value={baseData.region} 
            onChange={handleBaseChange} 
          />
        </form>

        {/* Sprachspezifische Daten */}
        <div className="mt-8 space-y-6">
          <h3 className="text-2xl font-bold">Ausführliche Beschreibung</h3>
          <div className="mb-4">
            <label className="block text-white mb-1">Jobbeschreibung</label>
            <ReactQuill 
              value={langData.job_description || ""}
              onChange={(value) => handleQuillChange("job_description", value)} 
              modules={quillModules}
              theme="snow"
              className="bg-white text-black"
            />
          </div>
          <div className="mb-4">
            <label className="block text-white mb-1">Qualifikationsanforderungen</label>
            <ReactQuill 
              value={langData.qualifications || ""}
              onChange={(value) => handleQuillChange("qualifications", value)} 
              modules={quillModules}
              theme="snow"
              className="bg-white text-black"
            />
          </div>
          <div className="mb-4">
            <label className="block text-white mb-1">Weitere Informationen</label>
            <ReactQuill 
              value={langData.additional_info || ""}
              onChange={(value) => handleQuillChange("additional_info", value)} 
              modules={quillModules}
              theme="snow"
              className="bg-white text-black"
            />
          </div>
          <div className="flex gap-4">
            <Select 
              label="Berufsbezeichnung" 
              name="beruf_id" 
              options={berufOptions}
              value={langData.beruf_id}
              onChange={handleLangChange}
            />
            <Select 
              label="Arbeitsform" 
              name="arbeitsform_id" 
              options={arbeitsformOptions}
              value={langData.arbeitsform_id}
              onChange={handleLangChange}
            />
          </div>
        </div>

        {/* Aktionen */}
        <div className="mt-8 flex justify-center gap-4">
          <button 
            onClick={handleSave} 
            className="px-6 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded transition"
          >
            Speichern
          </button>
          <button 
            onClick={() => navigate(-1)} 
            className="px-6 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded transition"
          >
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  );
};

export default StbEditStellenanzeige;
